import { type FanUsersMeResponse, fanUsersMe } from '@zealot/shared/apiClient';
import type { Client } from '@zealot/shared/apiClient/client';
import { safeLocalStorage } from '@zealot/shared/browser';
import type { StateCreator } from 'zustand';
import { STORAGE_KEYS } from '@/lib/auth/constants';
import { isValidUuid } from '@/lib/utils/profileLink';
import logger from '@/utils/logger';
import type { AppStore } from '../index';

// gloss
const rememberAccountKeys = (user: FanUsersMeResponse): void => {
  if (user.userID) {
    safeLocalStorage.setItem(STORAGE_KEYS.USER_ID, user.userID);
    safeLocalStorage.setItem(STORAGE_KEYS.USER_ID_SOURCE, 'profile');
  }
  if (user.email) safeLocalStorage.setItem(STORAGE_KEYS.USER_EMAIL, user.email);
};

export type CurrentUserSlice = {
  currentUser: {
    user: FanUsersMeResponse | null;
    isHydrated: boolean;
    error: Error | null;
    initialize: (client: Client) => Promise<void>;
    refreshMe: (client: Client) => Promise<void>;
    clear: () => void;
  };
};

const initial: Pick<CurrentUserSlice['currentUser'], 'user' | 'isHydrated' | 'error'> = {
  user: null,
  isHydrated: false,
  error: null,
};

// gloss
export const createCurrentUserSlice: StateCreator<AppStore, [['zustand/devtools', never]], [], CurrentUserSlice> = (
  set,
  get,
) => {
  // why: hydrate must capture and re-check the epoch on resolve — otherwise a stale /me response can silently
  // why: re-hydrate a logged-out session with the wrong user.
  let epoch = 0;

  const hydrate = async (client: Client): Promise<void> => {
    const ticket = ++epoch;
    try {
      const response = await fanUsersMe({ client });
      if (ticket !== epoch) return;
      if (response.error) {
        const message = (response.error as { message?: string })?.message ?? 'fanUsersMe request failed';
        throw new Error(message);
      }
      const data = response.data?.data;
      if (!data || !isValidUuid(data.uuid)) {
        throw new Error('fanUsersMe returned invalid payload');
      }
      rememberAccountKeys(data);
      set(
        (s) => ({ currentUser: { ...s.currentUser, user: data, isHydrated: true, error: null } }),
        false,
        'currentUser/hydrate',
      );
    } catch (err) {
      if (ticket !== epoch) return;
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error(error, { context: 'currentUser/hydrate' });
      set((s) => ({ currentUser: { ...s.currentUser, error, isHydrated: true } }), false, 'currentUser/hydrateError');
    }
  };

  return {
    currentUser: {
      ...initial,
      initialize: async (client) => {
        if (get().currentUser.isHydrated) return;
        await hydrate(client);
      },
      refreshMe: async (client) => hydrate(client),
      clear: () => {
        epoch++;
        set((s) => ({ currentUser: { ...s.currentUser, ...initial } }), false, 'currentUser/clear');
      },
    },
  };
};
